import type { QueryClient } from '@tanstack/react-query'
import { create } from 'zustand'

import { isApiError } from '@/api/client'
import { listJobs } from '@/api/jobs'
import { autorigAll, autorigAsset, getAssetPuppet, puppetKeys, type PuppetKind } from '@/api/puppets'
import { JobFailedError, skelKeys, waitForJob, type JobPhaseUpdate } from '@/api/skel'
import { toast } from '@/components/ui/toast'

/**
 * "Make it move" and "Make all move": start the job, then follow it (per-job SSE + polling via
 * `waitForJob`) OUTSIDE React, so a card that scrolls away or a tab switch never loses the
 * progress. Components read the live state from `useAutorigStore`; the server's asset index stays
 * the source of truth once a job ends (the lists are invalidated then).
 */

export interface LiveRig {
  status: 'rigging' | 'ready' | 'failed'
  jobId: number | null
  /** Parent-facing progress copy from the job ("Finding the arms…"). */
  msg: string | null
  pct: number | null
  error: string | null
  /** The new puppet, once known. */
  projectId: string | null
}

export interface LiveBatch {
  jobId: number
  status: 'running' | 'done' | 'failed'
  msg: string | null
  pct: number | null
  error: string | null
}

interface AutorigState {
  byUid: Record<string, LiveRig>
  batch: LiveBatch | null
  patchRig(uid: string, patch: Partial<LiveRig>): void
  setBatch(batch: LiveBatch | null): void
  reset(): void
}

export const useAutorigStore = create<AutorigState>()((set) => ({
  byUid: {},
  batch: null,
  patchRig: (uid, patch) =>
    set((s) => {
      const cur: LiveRig = s.byUid[uid] ?? { status: 'rigging', jobId: null, msg: null, pct: null, error: null, projectId: null }
      return { byUid: { ...s.byUid, [uid]: { ...cur, ...patch } } }
    }),
  setBatch: (batch) => set({ batch }),
  reset: () => set({ byUid: {}, batch: null }),
}))

/** Characters get the optional AI look (better joints); objects never call AI (P3). */
export const wantsAi = (kind: PuppetKind | null | undefined): boolean => kind !== 'object'

function errorText(err: unknown): string {
  if (err instanceof JobFailedError) return err.message || 'It could not be made to move.'
  return err instanceof Error && err.message ? err.message : 'It could not be made to move.'
}

function refreshLists(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: [...puppetKeys.all(), 'catalog'] })
  void qc.invalidateQueries({ queryKey: [...puppetKeys.all(), 'puppets'] })
  void qc.invalidateQueries({ queryKey: skelKeys.projects() })
}

const followed = new Set<number>()

/**
 * Rig one catalog asset. Resolves with the project id (or null when it failed). Safe to call twice:
 * a second click while the job runs just keeps following the same job.
 */
export async function makeItMove(qc: QueryClient, uid: string, opts: { kind?: PuppetKind | null; name?: string; force?: boolean } = {}): Promise<string | null> {
  const store = useAutorigStore.getState()
  const cur = store.byUid[uid]
  if (cur?.status === 'rigging' && cur.jobId != null) return null
  store.patchRig(uid, { status: 'rigging', msg: 'Starting…', pct: null, error: null, jobId: null })
  let jobId: number
  try {
    jobId = (await autorigAsset(uid, { ai: wantsAi(opts.kind), force: opts.force })).job_id
  } catch (err) {
    useAutorigStore.getState().patchRig(uid, { status: 'failed', msg: null, error: isApiError(err) ? err.message : errorText(err) })
    return null
  }
  useAutorigStore.getState().patchRig(uid, { jobId })
  refreshLists(qc)
  return followRig(qc, uid, jobId, opts.name)
}

async function followRig(qc: QueryClient, uid: string, jobId: number, name?: string): Promise<string | null> {
  if (followed.has(jobId)) return null
  followed.add(jobId)
  const onProgress = (u: JobPhaseUpdate): void => {
    if (u.status === 'queued') useAutorigStore.getState().patchRig(uid, { msg: 'Waiting for its turn…', pct: null })
    else useAutorigStore.getState().patchRig(uid, { msg: u.msg ?? 'Learning to move…', pct: u.pct })
  }
  try {
    const result = await waitForJob<{ project_id?: string }>(jobId, onProgress, undefined, { stream: true })
    let projectId = typeof result.project_id === 'string' ? result.project_id : null
    if (!projectId) {
      try {
        projectId = (await getAssetPuppet(uid)).link?.project_id ?? null
      } catch {
        projectId = null
      }
    }
    useAutorigStore.getState().patchRig(uid, { status: 'ready', msg: null, pct: 1, projectId })
    if (name) toast.success(`${name} can move now`)
    return projectId
  } catch (err) {
    useAutorigStore.getState().patchRig(uid, { status: 'failed', msg: null, error: errorText(err) })
    if (name) toast.error(`${name} could not be made to move`, { description: errorText(err) })
    return null
  } finally {
    followed.delete(jobId)
    refreshLists(qc)
    void qc.invalidateQueries({ queryKey: puppetKeys.asset(uid) })
  }
}

/* -------------------------------------------------------------------------- */
/* Make all move                                                               */
/* -------------------------------------------------------------------------- */

/** While the batch runs, the visible lists are refreshed this often so badges flip as rigs land. */
export const BATCH_REFRESH_MS = 5_000

export async function makeAllMove(qc: QueryClient, opts: { kinds?: PuppetKind[] } = {}): Promise<void> {
  const cur = useAutorigStore.getState().batch
  if (cur?.status === 'running') return
  let jobId: number
  try {
    jobId = (await autorigAll({ kinds: opts.kinds, ai: false })).job_id
  } catch (err) {
    toast.error('Could not start', { description: errorText(err) })
    return
  }
  await followBatch(qc, jobId)
}

/** Pick up a batch that is already running (started in another tab, or before a reload). */
export async function resumeRunningBatch(qc: QueryClient): Promise<void> {
  if (useAutorigStore.getState().batch?.status === 'running') return
  try {
    const page = await listJobs({ type: 'skel.autorig_all', status: 'running', limit: 1 })
    const job = page.items[0]
    if (job) await followBatch(qc, job.id)
  } catch {
    /* the jobs list is a convenience here */
  }
}

async function followBatch(qc: QueryClient, jobId: number): Promise<void> {
  if (followed.has(jobId)) return
  followed.add(jobId)
  const set = useAutorigStore.getState().setBatch
  set({ jobId, status: 'running', msg: 'Starting…', pct: null, error: null })
  const timer = setInterval(() => refreshLists(qc), BATCH_REFRESH_MS)
  try {
    await waitForJob(
      jobId,
      (u) => set({ jobId, status: 'running', msg: u.status === 'queued' ? 'Waiting for its turn…' : (u.msg ?? 'Working…'), pct: u.pct, error: null }),
      undefined,
      { stream: true, intervalMs: 2_000 },
    )
    set({ jobId, status: 'done', msg: 'Everyone can move now.', pct: 1, error: null })
    toast.success('All done', { description: 'Every character and prop has learned to move.' })
  } catch (err) {
    set({ jobId, status: 'failed', msg: null, pct: null, error: errorText(err) })
  } finally {
    clearInterval(timer)
    followed.delete(jobId)
    refreshLists(qc)
  }
}
